// src/components/TransferCertificate.js
import React, { useMemo, useRef, useState } from "react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import logo from "../assets/icon.png";

// ─────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────
const fmtDash = (d) => {
  if (!d) return "";
  try {
    return new Date(d)
      .toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
      .toUpperCase()
      .replace(/ /g, "-"); // 09-DEC-2023
  } catch {
    return String(d);
  }
};

const dobInWords = (d) => {
  if (!d) return "";
  try {
    const date = new Date(d);
    const day = date.getDate();
    const month = date.toLocaleString("en-IN", { month: "long" }).toUpperCase();
    const year = date.getFullYear();

    const ones = [
      "",
      "ONE",
      "TWO",
      "THREE",
      "FOUR",
      "FIVE",
      "SIX",
      "SEVEN",
      "EIGHT",
      "NINE",
      "TEN",
      "ELEVEN",
      "TWELVE",
      "THIRTEEN",
      "FOURTEEN",
      "FIFTEEN",
      "SIXTEEN",
      "SEVENTEEN",
      "EIGHTEEN",
      "NINETEEN",
    ];
    const tens = ["", "", "TWENTY", "THIRTY", "FORTY", "FIFTY"];

    const toWords = (n) => {
      if (n < 20) return ones[n];
      return tens[Math.floor(n / 10)] + (n % 10 ? " " + ones[n % 10] : "");
    };
    const yearWords = (y) => {
      const h = Math.floor(y / 100);
      const r = y % 100;
      return ones[h] + " HUNDRED" + (r ? " " + toWords(r) : "");
    };

    return `${toWords(day)} ${month} ${yearWords(year)}`;
  } catch {
    return "";
  }
};

// ─────────────────────────────────────────
// DATA EXTRACTOR — maps student JSON → TC fields
// ─────────────────────────────────────────
const extractTCData = (student = {}) => {
  const sd = student?.studentDetails || {};
  const parents = Array.isArray(student?.parentDetails)
    ? student.parentDetails
    : [];
  const father = parents.find((p) => p?.relation === "Father") || {};
  const mother = parents.find((p) => p?.relation === "Mother") || {};

  return {
    session: student?.session || sd?.session || "2025-26",
    tcNo: student?.tcNo || "",
    admissionNo: sd?.admission_no || student?.admission_no || "",
    registrationNo: student?.registrationNo || "",
    boardsRollNo: student?.boardsRollNo || "",
    studentName: (sd?.name || student?.name || "").toUpperCase(),
    motherName: (mother?.name || "").toUpperCase(),
    fatherName: (father?.name || "").toUpperCase(),
    nationality: (student?.nationality || "INDIAN").toUpperCase(),
    category: (sd?.category || student?.category || "").toUpperCase(),
    admissionDate: fmtDash(sd?.admissionDate || student?.admission_date),
    admissionClass: (
      sd?.admissionInClass ||
      student?.admissionInClass ||
      ""
    ).toUpperCase(),
    dob: fmtDash(sd?.dateOfBirth || student?.dateOfBirth),
    dobWords: dobInWords(sd?.dateOfBirth || student?.dateOfBirth),
    aadhar: sd?.aadhar_no || student?.aadhar_no || "",
    singleGirl:
      (sd?.gender || student?.gender || "") === "Female" ? "YES" : "NO",
    lastClass: (sd?.currentClass || student?.currentClass || "").toUpperCase(),
    isFailed: student?.isFailed ? "YES" : "NO",
    examResult: student?.examResult || "",
    subjects: student?.subjects || {
      compulsory: ["ENGLISH", "HINDI", "MATHEMATICS", "EVS", "SANSKRIT", "AI"],
      optional: ["YOGA"],
    },
    promotionStatus: student?.promotionStatus || "",
    promotionClassFigures: student?.promotionClassFigures || "",
    promotionClassWords: student?.promotionClassWords || "",
    feePaidUpTo: student?.feePaidUpTo || "",
    feeConcession: student?.feeConcession || "",
    workingDays: student?.workingDays || "",
    presentDays: student?.presentDays || "",
    nccDetails: student?.nccDetails || "",
    extraCurricular: student?.extraCurricular || "",
    generalConduct: student?.generalConduct || "GOOD",
    applicationDate: fmtDash(student?.applicationDate || new Date()),
    issueDate: fmtDash(student?.leftAt || student?.tc_date || new Date()),
    leavingReason: student?.leavingReason || "",
    remarks: student?.remarks || "",
  };
};

// ─────────────────────────────────────────
// SHARED ROW COMPONENTS
// ─────────────────────────────────────────
const BC = "#999"; // border colour

const Row = ({ no, label, children, minH = 24, noBottom = false }) => (
  <div
    style={{
      display: "flex",
      borderBottom: noBottom ? "none" : `1px solid ${BC}`,
      minHeight: `${minH}px`,
      alignItems: "stretch",
      fontSize: "15px",
    }}
  >
    <div style={styles.no}>{no}.</div>
    <div style={styles.lbl}>{label}</div>
    <div style={styles.val}>{children}</div>
  </div>
);

// ─────────────────────────────────────────
// TC DOCUMENT
// ─────────────────────────────────────────
const TCDocument = React.forwardRef(({ data }, ref) => (
  <div ref={ref} style={styles.page}>
    {/* watermark */}
    <div style={styles.wmWrap} aria-hidden="true">
      <img src={logo} alt="" style={styles.wmImg} />
    </div>

    <div style={{ position: "relative", zIndex: 1 }}>
      {/* ══ HEADER ══ */}
      <div style={styles.headerRow}>
        <div style={styles.logoBox}>
          <img src={logo} alt="logo" style={styles.logoImg} />
        </div>

        <div style={styles.headerText}>
          <div style={styles.schoolName}>ACHARYAKULAM</div>
          <div style={styles.schoolAddress}>
            TATA ROAD, NAMKUM, RANCHI 834010
          </div>
          <div style={styles.session}>Session (2025-26)</div>
        </div>

        <div style={styles.logoBox} />
      </div>

      <div className="mb-5" style={styles.pillWrap}>
        <div style={styles.pill}>TRANSFER CERTIFICATE</div>
      </div>

      {/* TC number */}
      {data.tcNo && (
        <div
          style={{
            fontSize: "15px",
            fontWeight: 800,
            marginLeft: "4px",
            marginTop: "1px",
          }}
        >
          {data.tcNo}
        </div>
      )}

      {/* TC No / School No / Affiliation */}
      <div style={styles.metaRow}>
        <span>
          TC No. : <span style={styles.metaUL} />
        </span>
        <span>
          School No. : <span style={styles.metaUL} />
        </span>
        <span>
          Affiliation No. : <span style={styles.metaUL} />
        </span>
      </div>

      {/* Adm / Reg / Boards */}
      <div style={styles.admRow}>
        <span>
          Adm. No. : <b>{data.admissionNo}</b>
        </span>
        <span>
          Registration No. : <b>{data.registrationNo}</b>
        </span>
        <span>
          Boards' Roll No. : <b>{data.boardsRollNo}</b>
        </span>
      </div>

      {/* ══ FIELDS TABLE ══ */}
      <div style={styles.table}>
        {/* 01 */}
        <Row no="01" label="Name of the Pupil">
          <b>{data.studentName}</b>
        </Row>

        {/* 02 — two sub-rows */}
        <div
          style={{
            display: "flex",
            borderBottom: `1px solid ${BC}`,
            fontSize: "10.5px",
          }}
        >
          <div style={styles.no}>02.</div>
          <div style={{ flex: 1 }}>
            <div
              style={{
                display: "flex",
                borderBottom: `1px solid ${BC}`,
                minHeight: "23px",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  minWidth: "175px",
                  borderRight: `1px solid ${BC}`,
                  padding: "2px 6px",
                  flexShrink: 0,
                }}
              >
                (a) Mother's Name
              </div>
              <div style={{ padding: "2px 8px", fontWeight: 700 }}>
                {data.motherName}
              </div>
            </div>
            <div
              style={{
                display: "flex",
                minHeight: "22px",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  minWidth: "175px",
                  borderRight: `1px solid ${BC}`,
                  padding: "2px 6px",
                  flexShrink: 0,
                }}
              >
                (b) Father's Name
              </div>
              <div style={{ padding: "2px 8px", fontWeight: 700 }}>
                {data.fatherName}
              </div>
            </div>
          </div>
        </div>

        {/* 03 + 04 */}
        <div
          style={{
            display: "flex",
            borderBottom: `1px solid ${BC}`,
            fontSize: "10.5px",
            minHeight: "23px",
            alignItems: "stretch",
          }}
        >
          <div style={styles.no}>03.</div>
          <div
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              borderRight: `1px solid ${BC}`,
              padding: "2px 8px",
              gap: 4,
            }}
          >
            Nationality : <b>{data.nationality}</b>
          </div>
          <div
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              padding: "2px 8px",
              gap: 4,
            }}
          >
            04. Whether S.C/S.T/OBC : <b>{data.category}</b>
          </div>
        </div>

        {/* 05 */}
        <Row no="05" label="Date of the first admission in school is">
          <b>{data.admissionDate}</b>&nbsp;&nbsp;&nbsp; in Class :{" "}
          <b>{data.admissionClass}</b>
        </Row>

        {/* 06 */}
        <Row
          no="06"
          label={
            <span>
              Date of birth (in Christian Era) as recorded in the Admission
              <br />
              Register (in figures)
            </span>
          }
          minH={38}
        >
          <span style={{ display: "flex", flexDirection: "column" }}>
            <b>{data.dob}</b>
            <span style={{ fontSize: "10px", marginTop: "1px" }}>
              &amp; (in words) <b>{data.dobWords}</b>
            </span>
          </span>
        </Row>

        {/* 07 + 08 */}
        <div
          style={{
            display: "flex",
            borderBottom: `1px solid ${BC}`,
            fontSize: "10.5px",
            minHeight: "23px",
            alignItems: "stretch",
          }}
        >
          <div style={styles.no}>07.</div>
          <div
            style={{
              flex: 2,
              display: "flex",
              alignItems: "center",
              borderRight: `1px solid ${BC}`,
              padding: "2px 8px",
              gap: 4,
            }}
          >
            Aadhar Card No. of the Pupil : <b>{data.aadhar}</b>
          </div>
          <div
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              padding: "2px 8px",
              gap: 4,
            }}
          >
            08. Single girl child ? <b>{data.singleGirl}</b>
          </div>
        </div>

        {/* 09 */}
        <Row no="09" label="Class in which the pupil studied last">
          <b>{data.lastClass}</b>
        </Row>

        {/* 10 */}
        <Row
          no="10"
          label="Whether the student was failed, in the same class (once or twice) ?"
        >
          <b>{data.isFailed}</b>
        </Row>

        {/* 11 */}
        <Row
          no="11"
          label="School/Board Annual Examination last taken with result"
        >
          <b>{data.examResult}</b>
        </Row>

        {/* 12 subjects */}
        <div
          style={{
            display: "flex",
            borderBottom: `1px solid ${BC}`,
            fontSize: "10.5px",
          }}
        >
          <div
            style={{
              ...styles.no,
              alignItems: "flex-start",
              paddingTop: "4px",
            }}
          >
            12.
          </div>
          <div style={{ flex: 1 }}>
            <div
              style={{
                padding: "2px 6px",
                borderBottom: `1px solid ${BC}`,
                fontWeight: 600,
              }}
            >
              Subject studied
            </div>
            <div
              style={{
                display: "flex",
                borderBottom: `1px solid ${BC}`,
                minHeight: "38px",
              }}
            >
              <div
                style={{
                  width: "88px",
                  borderRight: `1px solid ${BC}`,
                  padding: "3px 6px",
                  flexShrink: 0,
                }}
              >
                (a) Compulsory
              </div>
              <div style={{ flex: 1, padding: "3px 8px" }}>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "2px 18px",
                    wordBreak: "break-word",
                  }}
                >
                  {(data.subjects?.compulsory || []).map((sub, i) => (
                    <span key={i}>
                      ({i + 1}) <b>{sub}</b>
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <div
              style={{
                display: "flex",
                minHeight: "20px",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  width: "88px",
                  borderRight: `1px solid ${BC}`,
                  padding: "2px 6px",
                  flexShrink: 0,
                }}
              >
                (b) Optional
              </div>
              <div style={{ flex: 1, padding: "2px 8px", fontWeight: 700 }}>
                {(data.subjects?.optional || []).join(", ")}
              </div>
            </div>
          </div>
        </div>

        {/* 13 promotion */}
        <div
          style={{
            display: "flex",
            borderBottom: `1px solid ${BC}`,
            fontSize: "10.5px",
          }}
        >
          <div style={styles.no}>13.</div>
          <div style={{ flex: 1 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                padding: "2px 8px",
                borderBottom: `1px solid ${BC}`,
                minHeight: "23px",
                flexWrap: "wrap",
                gap: 4,
              }}
            >
              Whether qualified for promotion to higher class :{" "}
              <b>{data.promotionStatus}</b>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                padding: "2px 8px",
                minHeight: "20px",
                gap: 20,
              }}
            >
              <span>
                if so, to which class (in figures) :{" "}
                <b>{data.promotionClassFigures}</b>
              </span>
              <span>
                (in words): <b>{data.promotionClassWords}</b>
              </span>
            </div>
          </div>
        </div>

        {/* 14 */}
        <Row no="14" label="Month up to which the pupil has paid school fees">
          <b>{data.feePaidUpTo}</b>
        </Row>

        {/* 15 */}
        <Row
          no="15"
          label="Any fee concession availed of : If so, the nature of such concession"
        >
          <b>{data.feeConcession}</b>
        </Row>

        {/* 16 + 17 */}
        <div
          style={{
            display: "flex",
            borderBottom: `1px solid ${BC}`,
            fontSize: "10.5px",
            minHeight: "23px",
            alignItems: "stretch",
          }}
        >
          <div style={styles.no}>16.</div>
          <div
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              borderRight: `1px solid ${BC}`,
              padding: "2px 8px",
              gap: 4,
            }}
          >
            Total No. of School working days : <b>{data.workingDays}</b>
          </div>
          <div
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              padding: "2px 8px",
              gap: 4,
            }}
          >
            17. Total No. of days present : <b>{data.presentDays}</b>
          </div>
        </div>

        {/* 18 */}
        <Row no="18" label="Whether NCC cadet/Boy/Girl Guide (Give Details)">
          <b>{data.nccDetails}</b>
        </Row>

        {/* 19 */}
        <Row
          no="19"
          label={
            <span>
              Games played/Extra curricular activities in which the pupil took
              part
              <br />
              (Mention Achievement Level there in)
            </span>
          }
          minH={34}
        >
          <b>{data.extraCurricular}</b>
        </Row>

        {/* 20 */}
        <Row no="20" label="General Conduct">
          <b>{data.generalConduct}</b>
        </Row>

        {/* 21 */}
        <Row no="21" label="Date of application for Certificate">
          <b>{data.applicationDate}</b>
        </Row>

        {/* 22 */}
        <Row no="22" label="Date of issue of Certificate">
          <b>{data.issueDate}</b>
        </Row>

        {/* 23 */}
        <Row no="23" label="Reasons for leaving the School">
          <b>{data.leavingReason}</b>
        </Row>

        {/* 24 */}
        <Row no="24" label="Any other remarks" noBottom>
          <b>{data.remarks}</b>
        </Row>
      </div>
      {/* table bottom border */}
      <div style={{ borderBottom: `1px solid ${BC}` }} />

      {/* ══ SIGNATURES ══ */}
      <div style={styles.sigRow}>
        <div style={styles.sigCell}>
          <div style={styles.sigSpace} />
          <div style={styles.sigUnderline}>Prepared By</div>
        </div>

        <div style={styles.sigCell}>
          <div style={styles.sigSpace} />
          <div style={styles.sigUnderline}>Class Teacher</div>
        </div>

        <div style={styles.sigCell}>
          <div style={styles.sigSpace} />
          <div style={styles.sigUnderline}>Checked By</div>
          <div
            style={{
              paddingTop: "1px",
              fontSize: "9px",
            }}
          >
            (Name & Designation)
          </div>
        </div>

        <div style={styles.sigCell}>
          <div style={styles.sigSpace} />
          <div style={styles.sigUnderline}>Principal</div>
          <div
            style={{
              paddingTop: "1px",
              fontSize: "9px",
            }}
          >
            (Seal)
          </div>
        </div>
      </div>
    </div>
  </div>
));

// ─────────────────────────────────────────
// EXPORTED PAGE COMPONENT
// ─────────────────────────────────────────
export default function TransferCertificatePage({
  student,
  buttonLabel = "Download TC",
}) {
  const certRef = useRef(null);
  const [downloading, setDownloading] = useState(false);

  const data = useMemo(() => extractTCData(student), [student]);

  const downloadPdf = async () => {
    if (!certRef.current) return;
    setDownloading(true);
    try {
      const canvas = await html2canvas(certRef.current, {
        scale: 3,
        useCORS: true,
        backgroundColor: "#ffffff",
      });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pageW = 210;
      const margin = 8;
      const usableW = pageW - margin * 2;
      const usableH = 297 - margin * 2;
      const ratio = canvas.width / canvas.height;
      let rW = usableW;
      let rH = rW / ratio;
      if (rH > usableH) {
        rH = usableH;
        rW = rH * ratio;
      }
      pdf.addImage(imgData, "PNG", (pageW - rW) / 2, margin, rW, rH);
      pdf.save(`${data.admissionNo || "student"}-TC.pdf`);
    } catch (e) {
      console.error("TC download failed:", e);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="w-100">
      {/* hidden render target */}
      <div style={{ position: "fixed", left: "-99999px", top: 0, zIndex: -1 }}>
        <TCDocument ref={certRef} data={data} />
      </div>

      <div className="mt-2 mb-2">
        <span
          className="badge bg-danger-soft cursor-pointer px-4 rounded-3 btn-py-sm"
          onClick={downloading ? undefined : downloadPdf}
          style={{
            opacity: downloading ? 0.6 : 1,
            cursor: downloading ? "not-allowed" : "pointer",
          }}
        >
          <i className="bi bi-download me-1" />
          {downloading ? "Generating..." : buttonLabel}
        </span>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────
// STYLES
// ─────────────────────────────────────────
const styles = {
  page: {
    width: "794px",
    minHeight: "1123px",
    background: "#fff",
    fontFamily: "'Times New Roman', Times, serif",
    color: "#111",
    boxSizing: "border-box",
    padding: "18px 22px",
    position: "relative",
  },
  headerRow: {
    display: "grid",
    gridTemplateColumns: "90px 1fr 90px",
    alignItems: "center",
  },
  pillWrap: { display: "flex", justifyContent: "center", marginTop: "13px" },
  pill: {
    border: "2px solid #111",
    borderRadius: "15px",
    padding: "6px 70px",
    fontWeight: 700,
    fontSize: "16px",
  },

  logoBox: {
    width: "90px",
    height: "90px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  logoImg: { width: "95px", height: "95px", objectFit: "contain" },
  headerText: { textAlign: "center" },
  schoolName: { fontSize: "30px", fontWeight: 800, letterSpacing: "1px" },
  schoolAddress: { fontSize: "23px", marginTop: "-10px" },
  session: { fontSize: "23px", marginTop: "-10px" },
  wmWrap: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    pointerEvents: "none",
    zIndex: 0,
  },
  wmImg: {
    width: "380px",
    height: "380px",
    objectFit: "contain",
    opacity: 0.07,
    filter: "grayscale(100%)",
  },

  /* header */
  hdrGrid: {
    display: "grid",
    gridTemplateColumns: "108px 1fr 85px",
    alignItems: "center",
    marginBottom: "6px",
  },
  hdrLeft: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "3px",
  },
  hintBadge: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    border: "1px solid #666",
    fontSize: "8.5px",
    textAlign: "center",
    lineHeight: 1.4,
    width: "98px",
  },
  hintCell: { padding: "2px 3px" },
  schoolName: { fontSize: "30px", fontWeight: 900, letterSpacing: "2px" },
  schoolAddr: { fontSize: "13px", fontWeight: 600, marginTop: "-2px" },
  affiliated: { fontSize: "10.5px", color: "#444" },
  rLogoImg: { width: "78px", height: "78px", objectFit: "contain" },

  /* title */
  titleBox: {
    border: "2px solid #111",
    padding: "3px 38px",
    fontSize: "13.5px",
    fontWeight: 800,
    letterSpacing: "2px",
    display: "inline-block",
  },

  /* meta */
  metaRow: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: "10.5px",
    margin: "2px 0",
    padding: "0 2px",
  },
  metaUL: {
    display: "inline-block",
    borderBottom: "1px solid #888",
    minWidth: "80px",
  },
  admRow: {
    display: "flex",
    gap: "20px",
    fontSize: "10.5px",
    borderTop: "1px solid #aaa",
    borderBottom: "1px solid #aaa",
    padding: "2px 4px",
  },

  /* table & rows */
  table: { border: `1px solid ${BC}`, borderBottom: "none" },
  no: {
    minWidth: "28px",
    maxWidth: "28px",
    borderRight: `1px solid ${BC}`,
    padding: "2px 3px",
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "15px",
    flexShrink: 0,
  },
  lbl: {
    minWidth: "228px",
    maxWidth: "228px",
    borderRight: `1px solid ${BC}`,
    padding: "2px 6px",

    display: "block", // 🔥 change from flex → block
    fontSize: "15px",

    wordBreak: "break-word", // ✅ wrap long text
    overflowWrap: "break-word", // ✅ modern wrapping
    whiteSpace: "normal", // ✅ allow multiline
    lineHeight: "1.2",
  },

  val: {
    flex: 1,
    padding: "2px 8px",
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "0 8px",
    fontSize: "15px",

    wordBreak: "break-word", // ✅ fix overflow
    overflowWrap: "break-word", // ✅ modern support
    whiteSpace: "normal", // ✅ allow wrapping
  },

  /* footer */
  certNote: {
    fontSize: "9px",
    color: "#444",
    textAlign: "center",
    marginTop: "4px",
    marginBottom: "6px",
  },
  sigRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr 1fr",
    gap: "0 15px",
    marginTop: "40px",
  },
  sigCell: { display: "flex", flexDirection: "column", alignItems: "center" },
  sigSpace: { height: "36px" },
  sigUnderline: {
    borderTop: "1px solid #555",
    paddingTop: "3px",
    fontSize: "10px",
    fontWeight: 600,
    width: "100%",
    textAlign: "center",
  },
};
